// ─── Soci — l'elenco che risponde mentre si scrive ───────────────────────────
//
// 🎯 È metà del criterio della Fase 5: «si cerca un socio digitando e la lista
// risponde mentre si scrive su ~2.800 soci».
//
// ⚖️ E «risponde» non vuol dire solo «è veloce»: vuol dire che la lista NON
// LAMPEGGIA. Una ricerca che svuota la tabella a ogni battuta e la ricompone
// sembra lenta anche quando risponde in trenta millisecondi, perché quello che
// l'occhio misura è il vuoto, non l'attesa.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatCents, livelloInParole, type FasciaDiLivello, type SocioInElenco } from '@pmo/shared';
import { esporta, usaScalaDeiLivelli, usaSoci } from '../../features/soci/dati.js';
import { useSessione } from '../../features/auth/sessione.js';
import { Attesa, Guasto, Vuoto } from '../../components/pezzi.js';
import { SchedaSocio } from './SchedaSocio.js';
import { NuovoSocio } from './NuovoSocio.js';
import { ImportaFile } from './ImportaFile.js';

/**
 * Quanto si aspetta prima di chiedere al server.
 *
 * ⚖️ 180 ms è sotto la soglia in cui si percepisce un ritardo, e sopra il tempo
 * fra due battute di chi digita in fretta: chi scrive «Rossi» fa **una**
 * domanda, non cinque. Senza, la segreteria che cerca un cognome lungo manda
 * otto richieste e ne guarda una.
 */
const ATTESA_TASTIERA = 180;

/** Lo spazio unificatore. Costruito, non scritto: nel sorgente si vede. */
const SPAZIO_FERMO = String.fromCharCode(160);

export function Soci(): React.JSX.Element {
  const { profilo } = useSessione();
  const [parametri, setParametri] = useSearchParams();
  const [scritto, setScritto] = useState(parametri.get('q') ?? '');
  const [cercato, setCercato] = useState(scritto);
  const [archiviati, setArchiviati] = useState(false);
  const [pagina, setPagina] = useState<string | null>(null);
  /** Voce 26: «Aggiungi un socio» e «Importa» si aprono in cima, uno per volta. */
  const [pannello, setPannello] = useState<'nuovo' | 'importa' | null>(null);
  const [fatto, setFatto] = useState<string | null>(null);
  const aperto = parametri.get('socio');

  useEffect(() => {
    const t = setTimeout(() => {
      setCercato(scritto);
      // Cambiando ricerca si torna alla prima pagina: restare alla terza
      // mostrerebbe un elenco vuoto per una ricerca che ha dei risultati.
      setPagina(null);
    }, ATTESA_TASTIERA);
    return () => clearTimeout(t);
  }, [scritto]);

  const elenco = usaSoci({ q: cercato, archived: archiviati, cursor: pagina });
  const fasce = usaScalaDeiLivelli().data?.fasce ?? [];
  const puòVedereAnagrafica = profilo?.permissions.includes('view_members_anagrafica') ?? false;
  const puòImportare = profilo?.permissions.includes('manage_settings') ?? false;

  const apri = (id: string | null) => {
    const p = new URLSearchParams(parametri);
    if (id) p.set('socio', id);
    else p.delete('socio');
    setParametri(p, { replace: true });
  };

  const items = elenco.data?.items ?? [];

  return (
    <section className="soci">
      {/* 📋 Voce 26: i gesti in alto a destra, accanto al titolo. «Aggiungi»
          pieno perché è quello che si fa di più; «Importa» ed «Esporta»
          bordati. Niente più testo sottolineato. */}
      <header className="testata testata-soci">
        <h2>
          Soci{' '}
          <span className="conto tenue">
            {elenco.data?.total === null || elenco.data?.total === undefined
              ? // Uno spazio unificatore tiene alta la riga anche quando non c'e'
                // niente da scrivere: senza, l'intestazione salta su e giu' a ogni
                // ricerca. Si costruisce, invece di scriverlo: nel sorgente un
                // carattere invisibile e' una cosa che nessuno sa piu' perche' c'e'.
                SPAZIO_FERMO
              : `${elenco.data.total} ${elenco.data.total === 1 ? 'socio' : 'soci'}${cercato ? ' per questa ricerca' : ''}`}
          </span>
        </h2>
        <div className="gesti-soci">
          {puòVedereAnagrafica && (
            <button
              type="button"
              onClick={() => {
                setFatto(null);
                setPannello('nuovo');
              }}
            >
              Aggiungi un socio
            </button>
          )}
          {puòImportare && (
            <button type="button" className="bordato" onClick={() => setPannello('importa')}>
              Importa
            </button>
          )}
          <MenuEsporta filtro={{ q: cercato, archived: archiviati }} />
        </div>
      </header>

      {pannello === 'nuovo' && (
        <NuovoSocio
          chiudi={() => setPannello(null)}
          fatto={(nome) => {
            setFatto(nome);
            setPannello(null);
          }}
        />
      )}
      {pannello === 'importa' && <ImportaFile chiudi={() => setPannello(null)} />}
      {fatto && <p className="riuscito">{fatto} è nell’anagrafica.</p>}

      <div className="barra-soci">
        <input
          type="search"
          className="cerca-soci"
          aria-label="Cerca"
          placeholder="Cerca per nome, cognome, telefono o email"
          value={scritto}
          autoFocus
          onChange={(e) => setScritto(e.target.value)}
        />
        <label className="spunta">
          <input
            type="checkbox"
            checked={archiviati}
            onChange={(e) => {
              setArchiviati(e.target.checked);
              setPagina(null);
            }}
          />
          <span>Mostra gli archiviati</span>
        </label>
      </div>

      <Guasto errore={elenco.error} />

      {/* ⭐ `isPending` e non `isFetching`: durante una ricerca nuova i dati di
          prima restano a schermo (`placeholderData`), e mostrare «sto
          leggendo» sopra una lista piena sarebbe rumore. Lo scheletro compare
          solo la PRIMA volta, quando davvero non c'è niente da guardare. */}
      {elenco.isPending ? (
        <Attesa cosa="i soci" />
      ) : items.length === 0 ? (
        <Vuoto
          titolo={cercato ? 'Nessuno con questo nome' : 'Nessun socio'}
          perché={
            cercato
              ? 'La ricerca guarda nome, cognome, telefono ed email. Prova con meno lettere.'
              : 'L’anagrafica è vuota.'
          }
        />
      ) : (
        <div className={`tabella-scorre${elenco.isFetching ? ' in-aggiornamento' : ''}`}>
          <table className="elenco-soci">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Telefono</th>
                <th>Email</th>
                <th>Livello</th>
                <th className="cifra">Borsellino</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <Riga key={s.id} socio={s} fasce={fasce} apri={() => apri(s.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(pagina !== null || elenco.data?.nextCursor) && (
        <div className="pagine">
          <button type="button" className="piatto" disabled={pagina === null} onClick={() => setPagina(null)}>
            ↩ Torna all’inizio
          </button>
          <button
            type="button"
            className="piatto"
            disabled={!elenco.data?.nextCursor}
            onClick={() => setPagina(elenco.data?.nextCursor ?? null)}
          >
            Altri →
          </button>
        </div>
      )}

      {aperto && <SchedaSocio id={aperto} chiudi={() => apri(null)} />}
    </section>
  );
}

function Riga({
  socio,
  fasce,
  apri,
}: {
  socio: SocioInElenco;
  fasce: readonly FasciaDiLivello[];
  apri: () => void;
}): React.JSX.Element {
  return (
    <tr className={socio.active ? '' : 'spento'} onClick={apri}>
      <td>
        <button type="button" className="piatto nome-socio">
          {socio.displayName}
        </button>
        {socio.archived && <span className="etichetta"> archiviato</span>}
        {!socio.active && !socio.archived && <span className="etichetta"> disattivato</span>}
      </td>
      <td>{socio.phone ?? '—'}</td>
      <td>{socio.email ?? '—'}</td>
      {/* 🎾 A parole, dalla scala del circolo (25/09/2026, scelta B del mockup). */}
      <td>{livelloInParole(fasce, socio.level)}</td>
      {/* 💶 Una cella vuota vuol dire «non ha mai avuto un movimento», che NON
          è «zero euro»: le due cose si scrivono in modo diverso apposta. */}
      <td className={`cifra ${saldoClasse(socio.walletBalanceCents)}`}>
        {socio.walletBalanceCents === null ? '—' : formatCents(socio.walletBalanceCents)}
      </td>
    </tr>
  );
}

export function saldoClasse(cents: number | null): string {
  if (cents === null || cents === 0) return 'tenue';
  return cents > 0 ? 'saldo-pieno' : 'saldo-rosso';
}

/**
 * 📋 Voce 26: «Esporta» diventa un bottone solo, con dentro CSV ed Excel.
 * Si chiude toccando fuori o con Esc, come ogni menu.
 */
function MenuEsporta({ filtro }: { filtro: { q: string; archived: boolean } }): React.JSX.Element {
  const [aperto, setAperto] = useState(false);
  const qui = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!aperto) return;
    const fuori = (e: MouseEvent) => {
      if (!qui.current?.contains(e.target as Node)) setAperto(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAperto(false);
    };
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuori);
      document.removeEventListener('keydown', esc);
    };
  }, [aperto]);

  const scegli = (formato: 'csv' | 'xlsx') => {
    setAperto(false);
    void esporta(formato, filtro);
  };
  return (
    <div className="menu-esporta" ref={qui}>
      <button
        type="button"
        className="bordato"
        aria-haspopup="true"
        aria-expanded={aperto}
        onClick={() => setAperto(!aperto)}
      >
        Esporta ▾
      </button>
      {aperto && (
        <div className="menu-esporta-voci" role="menu">
          <button type="button" role="menuitem" onClick={() => scegli('csv')}>
            Esporta CSV
          </button>
          <button type="button" role="menuitem" onClick={() => scegli('xlsx')}>
            Esporta Excel
          </button>
        </div>
      )}
    </div>
  );
}
